import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import GenerateNowButton from '../components/GenerateNowButton';
import { fmtGeneratedAt } from '../utils/replenishment';
import {
  CatalogDetail, CatalogListResponse, CatalogMeta, CatalogParams, CatalogQuery, CatalogRow, CodV3,
  replenishmentCatalogApi as catalogApi,
} from '../services/replenishmentCatalogApi';

/**
 * Catalogação v3: proposta de cod_distribuição D1 (online) / D2 (omnicanal).
 * Mesma lista que a PCDIGA recebe por CSV (scripts/14_listas_catalogacao.py).
 */

const PAGE_SIZE = 50;
const DEFAULT_ORDERING = 'lista';
const DEFAULT_MIN_DAYS = 10;
const STORES = ['A000', 'B000', 'C000', 'D000', 'E000', 'F000'];

const MOTIVO_ORDER = ['procura_lojas', 'restock', 'fim_de_vida_sem_stock', 'procura_residual', 'volumoso'];
const MOTIVO_LABEL: Record<string, string> = {
  procura_lojas: 'Procura em ≥ 2 lojas',
  restock: 'Procura em ≥ 2 lojas, sem stock no T000 (restock)',
  fim_de_vida_sem_stock: 'Fim de vida sem stock no T000',
  procura_residual: 'Procura em < 2 lojas',
  volumoso: 'Volumoso',
};
const COD_LABEL: Record<CodV3, string> = { D2: 'Omnicanal · D2', D1: 'Online · D1' };

// ordenação por coluna: [padrão, alternativa]
const SORTS: Record<string, [string, string?]> = {
  sku: ['sku_asc'],
  nome: ['nome_asc'],
  lojas_procura: ['lojas_procura_desc', 'lojas_procura_asc'],
  procura: ['procura_desc', 'procura_asc'],
  lojas: ['lojas_asc', 'lojas_desc'],
  stock_t: ['stock_t_desc', 'stock_t_asc'],
  vel: ['vel_desc'],
};

const ORDERINGS = [DEFAULT_ORDERING, ...Object.values(SORTS).flat().filter((o): o is string => !!o)];

const fmtInt = (v: number | null | undefined): string =>
  v === null || v === undefined ? '-' : v.toLocaleString('pt-PT', { maximumFractionDigits: 0 });
const fmtRate = (v: number | null | undefined): string =>
  v === null || v === undefined ? '-' : v.toLocaleString('pt-PT', { maximumFractionDigits: 2 });
const fmtDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

const Badge: React.FC<{ color: string; children: React.ReactNode; title?: string }> = ({ color, children, title }) => (
  <span className={`badge bg-${color}`} title={title} style={{ whiteSpace: 'normal' }}>{children}</span>
);
const CodBadge: React.FC<{ cod: CodV3 | null | undefined }> = ({ cod }) => (
  cod ? <Badge color={cod === 'D2' ? 'success' : 'light text-dark border'}>{COD_LABEL[cod]}</Badge>
    : <span className="text-muted">-</span>
);

/** Seis quadrados A–F: verde = loja com stock; vermelho claro = sem stock mas com procura. */
const StoreDots: React.FC<{ row: Pick<CatalogRow, 'lojas' | 'lojas_com_stock' | 'lojas_procura_v3'> }> = ({ row }) => {
  const byStore = new Map(row.lojas.map((s) => [s.loja, s]));
  const withDemand = new Set(row.lojas_procura_v3 ?? []);
  return (
    <span className="d-inline-flex gap-1" aria-label={`${row.lojas_com_stock} de 6 lojas com stock`}>
      {STORES.map((code) => {
        const stock = byStore.get(code)?.stock ?? 0;
        const has = stock > 0;
        const demand = withDemand.has(code);
        return (
          <span key={code}
                title={`${code}: stock ${fmtInt(stock)} · ${demand ? 'com' : 'sem'} procura (84 dias)`}
                style={{
                  width: 16, height: 16, borderRadius: 3, fontSize: 9, lineHeight: '16px', textAlign: 'center',
                  color: has ? '#fff' : '#6c757d',
                  background: has ? '#198754' : demand ? '#f8d7da' : '#e9ecef',
                  border: !has && demand ? '1px solid #dc3545' : '1px solid transparent',
                }}>
            {code[0]}
          </span>
        );
      })}
    </span>
  );
};

const RulesPanelV3: React.FC<{ p: Partial<CatalogParams> }> = ({ p }) => {
  if (!p.v3_min_stores) return <div className="text-muted small">Parâmetros v3 indisponíveis neste snapshot.</div>;
  const j = (xs?: string[]) => (xs && xs.length ? xs.join(', ') : '-');
  const days = p.t_min_duoos_days ?? DEFAULT_MIN_DAYS;
  const items: [string, React.ReactNode][] = [
    ['Volumoso → D1', <>Categoria volumosa ({j(p.bulky_categories)}) ou TV de {p.bulky_tv_min_inches}" ou mais. Não cabe nas lojas: fica online.</>],
    ['Fim de vida sem stock no T000 → D1', <>Procura em {p.v3_min_stores} ou mais lojas, mas estado {j(p.downgrade_states)} e sem stock no T000: não se repõe, fica online.</>],
    ['Restock → D2', <>Procura em {p.v3_min_stores} ou mais lojas e sem stock no T000: fica omnicanal, mas só chega às lojas depois de aumentar o stock geral (&quot;precisa de restock&quot;).</>],
    ['Procura em lojas → D2', <>Procura (levantamentos + balcão) em {p.v3_min_stores} ou mais lojas nos últimos {p.v3_window_days} dias. Um D2 vai para todas as lojas.</>],
    ['Procura residual → D1', <>Todos os outros artigos: procura em menos de {p.v3_min_stores} lojas.</>],
  ];
  return (
    <>
      <p className="text-muted small mb-2">
        Cada artigo é avaliado por esta ordem e <strong>ganha a primeira regra que se verifica</strong>.
        <strong> D2 = omnicanal</strong>, <strong>D1 = online</strong>. <strong>T000 abaixo de {fmtInt(days)} dias</strong> =
        stock do T000 inferior a {fmtInt(days)} dias de vendas com envio: é só um aviso (a reposição das lojas fica limitada),
        não muda o código.
      </p>
      <ol className="small mb-0">
        {items.map(([name, text]) => <li key={name}><strong>{name}:</strong> {text}</li>)}
      </ol>
    </>
  );
};

/* --------------------------------- Detalhe --------------------------------- */

const DetailPanel: React.FC<{ sku: string; snapshot?: string; onClose: () => void }> = ({ sku, snapshot, onClose }) => {
  const [detail, setDetail] = useState<CatalogDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    catalogApi.detail(sku, snapshot)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Erro ao carregar'); });
    return () => { cancelled = true; };
  }, [sku, snapshot]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const demand = new Map((detail?.procura_loja_84d_por_loja ?? []).map((d) => [d.loja, d.procura]));
  const history = (detail?.historico ?? []).filter((h) => h.cod_distribuicao_v3);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1040 }} />
      <aside role="dialog" aria-label={`Detalhe de ${sku}`}
             style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(860px, 100vw)', zIndex: 1050,
               background: '#fff', overflowY: 'auto', boxShadow: '-4px 0 16px rgba(0,0,0,.2)' }}>
        <div className="p-3 border-bottom d-flex justify-content-between align-items-start sticky-top bg-white">
          <div>
            <div className="text-muted small">{sku}</div>
            <h5 className="mb-1">{detail?.nome ?? (error ? 'Erro' : 'A carregar…')}</h5>
            {detail && <CodBadge cod={detail.cod_distribuicao_v3} />}
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose} aria-label="Fechar">✕</button>
        </div>
        <div className="p-3">
          {error && <div className="alert alert-danger">{error}</div>}
          {detail && !detail.cod_distribuicao_v3 && (
            <div className="alert alert-warning">Este snapshot não tem a classificação v3.</div>
          )}
          {detail && detail.cod_distribuicao_v3 && (
            <>
              <div className="mb-3">
                <div className="fw-semibold">{detail.motivo_v3_texto ?? detail.motivo_v3}</div>
                {detail.ressalva_v3 && <div className="small text-muted">{detail.ressalva_v3}</div>}
                <div className="small mt-1">
                  {detail.precisa_restock_v3 && <span className="me-1"><Badge color="warning text-dark">Precisa de restock</Badge></span>}
                  {detail.t_protegido_v3 && <span className="me-1"><Badge color="danger">T000 abaixo de {DEFAULT_MIN_DAYS} dias</Badge></span>}
                </div>
              </div>
              <div className="row g-2 mb-3">
                {([
                  ['Lojas com procura (84d)', `${fmtInt(detail.n_lojas_procura_v3)} (${(detail.lojas_procura_v3 ?? []).join(', ') || '-'})`],
                  ['Encomendas loja (84d)', fmtInt(detail.procura_loja_v3)],
                  ['Stock T000', fmtInt(detail.stock_T)],
                  ['Compra em curso T000', fmtInt(detail.compra_em_curso_T)],
                  ['Em trânsito T000', fmtInt(detail.transito_T)],
                  ['Vendas/dia T000 (envio, 28d)', fmtRate(detail.vel_T_28d)],
                  ['Vendas/dia total (28d)', fmtRate(detail.vel_total_28d)],
                  ['Envio / Levant. / Balcão (28d)', `${fmtInt(detail.pedidos_envio)} / ${fmtInt(detail.pedidos_levant)} / ${fmtInt(detail.pedidos_balcao)}`],
                ] as [string, string][]).map(([label, value]) => (
                  <div className="col-6" key={label}>
                    <div className="text-muted small">{label}</div>
                    <div className="fw-semibold">{value}</div>
                  </div>
                ))}
              </div>
              <h6>Por loja</h6>
              <table className="table table-sm small">
                <thead><tr><th>Loja</th><th className="text-end">Stock hoje</th><th className="text-end">Procura 84d</th></tr></thead>
                <tbody>
                  {detail.lojas.map((s) => (
                    <tr key={s.loja}>
                      <td>{s.loja}</td>
                      <td className="text-end">{fmtInt(s.stock)}</td>
                      <td className="text-end">{demand.has(s.loja) ? fmtInt(demand.get(s.loja)) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <h6>Histórico (v3)</h6>
              {history.length === 0 ? <p className="text-muted small">Sem histórico v3.</p> : (
                <table className="table table-sm small">
                  <thead><tr><th>Snapshot</th><th>Código</th><th>Motivo</th><th className="text-end">Lojas c/ procura</th></tr></thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.snapshot_date}>
                        <td>{fmtDate(h.snapshot_date)}</td>
                        <td><CodBadge cod={h.cod_distribuicao_v3} /></td>
                        <td>{h.motivo_v3 ? MOTIVO_LABEL[h.motivo_v3] ?? h.motivo_v3 : '-'}</td>
                        <td className="text-end">{fmtInt(h.n_lojas_procura_v3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
};

/* ---------------------------------- Página --------------------------------- */

const ReplenishmentCatalogV3Page: React.FC = () => {
  const [meta, setMeta] = useState<CatalogMeta | null>(null);
  const [params, setParams] = useSearchParams();
  const list = (key: string): string[] => (params.get(key) || '').split(',').filter(Boolean);
  const codFilter = list('cod');
  const motivoFilter = list('motivo');
  const restock = params.get('restock') === '1';
  const tProtegido = params.get('t_protegido') === '1';
  const cat1 = params.get('cat1') || '';
  const estado = params.get('estado') || '';
  const search = params.get('q') || '';
  const orderingParam = params.get('ordering') || DEFAULT_ORDERING;
  // ordenações de outras versões (links antigos) não valem na v3
  const ordering = ORDERINGS.includes(orderingParam) ? orderingParam : DEFAULT_ORDERING;
  const page = Math.max(0, (parseInt(params.get('page') || '1', 10) || 1) - 1);
  const selectedSku = params.get('sku');
  const snapshot = params.get('snapshot') || meta?.snapshots[0]?.snapshot_date || '';
  const [searchInput, setSearchInput] = useState(search);

  const update = useCallback((patch: Record<string, string | undefined>, opts?: { keepPage?: boolean; replace?: boolean }) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!opts?.keepPage && !('page' in patch)) next.delete('page');
      Object.entries(patch).forEach(([k, v]) => {
        const isDefault = (k === 'ordering' && v === DEFAULT_ORDERING) || (k === 'page' && v === '1');
        if (!v || isDefault) next.delete(k); else next.set(k, v);
      });
      return next;
    }, { replace: opts?.replace });
  }, [setParams]);

  const [data, setData] = useState<CatalogListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(false);
  const requestId = useRef(0);

  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    catalogApi.meta()
      .then((m) => setMeta(m))
      .catch((e) => setError(e?.response?.data?.error || e?.message || 'Erro ao carregar metadados'));
  }, [reloadTick]);

  useEffect(() => {
    const value = searchInput.trim();
    if (value === search) return undefined;
    const t = setTimeout(() => update({ q: value || undefined }, { replace: true }), 350);
    return () => clearTimeout(t);
  }, [searchInput, search, update]);
  useEffect(() => { setSearchInput(search); }, [search]);

  const query: CatalogQuery = useMemo(() => ({
    snapshot: snapshot || undefined,
    versao: 'v3',
    cod: codFilter,
    motivo: motivoFilter,
    restock,
    t_protegido: tProtegido,
    cat1: cat1 || undefined,
    estado: estado || undefined,
    search: search || undefined,
    ordering,
  }), [snapshot, params]);  // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!snapshot) return;
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await catalogApi.list({ ...query, limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      if (id === requestId.current) setData(res);
    } catch (e: any) {
      if (id === requestId.current) setError(e?.response?.data?.error || e?.message || 'Erro ao carregar dados');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [query, page, snapshot]);
  useEffect(() => { load(); }, [load, reloadTick]);

  const toggle = (key: 'cod' | 'motivo', current: string[], value: string) => {
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    update({ [key]: next.join(',') || undefined });
  };
  const resetFilters = () => {
    setSearchInput('');
    update({ cod: undefined, motivo: undefined, restock: undefined, t_protegido: undefined, cat1: undefined,
      estado: undefined, q: undefined });
  };
  const hasFilters = codFilter.length + motivoFilter.length > 0 || restock || tProtegido || !!(cat1 || estado || search);
  const minDays = meta?.parametros.t_min_duoos_days ?? DEFAULT_MIN_DAYS;
  const setPage = (fn: number | ((p: number) => number)) => {
    const n = typeof fn === 'function' ? fn(page) : fn;
    update({ page: String(n + 1) }, { keepPage: true });
  };

  const sortBy = (col: string) => {
    const [first, second] = SORTS[col];
    update({ ordering: ordering === first && second ? second : first });
  };
  const sortMark = (col: string): string => {
    const [first, second] = SORTS[col];
    if (ordering === first) return first.endsWith('_asc') ? ' ▲' : ' ▼';
    if (second && ordering === second) return second.endsWith('_asc') ? ' ▲' : ' ▼';
    return '';
  };
  const Th: React.FC<{ col: string; className?: string; title?: string; children: React.ReactNode }> = ({ col, className, title, children }) => (
    <th className={className} title={title} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => sortBy(col)}>
      {children}{sortMark(col)}
    </th>
  );

  const total = data?.count ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const motivos = meta?.motivos_v3 ?? MOTIVO_LABEL;
  const generatedAt = data?.calculated_at
    ?? meta?.snapshots.find((s) => s.snapshot_date === snapshot)?.calculated_at ?? null;

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h2 className="mb-1">🏬 Replenishment — Catalogação omnicanal / online</h2>
          <p className="text-muted small mb-2">
            Proposta de <strong>cod_distribuição</strong> (regra v3): <strong>D2 = omnicanal</strong>, com procura em
            {' '}{meta?.parametros.v3_min_stores ?? 2} ou mais lojas nos últimos {meta?.parametros.v3_window_days ?? 84} dias;
            {' '}<strong>D1 = online</strong>. Volumosos e artigos em fim de vida sem stock no T000 ficam D1.{' '}
            <Link to="/replenishment">Hit rate das lojas</Link>
          </p>
        </div>
        <div className="text-end">
          <div className="text-muted small mb-1" data-testid="generated-at">
            {generatedAt ? `Gerada em ${fmtGeneratedAt(generatedAt)}` : ''}
          </div>
          <GenerateNowButton job="catalog_classification" onFinished={() => setReloadTick((t) => t + 1)} />
        </div>
      </div>
      <div className="d-flex justify-content-end mb-2">
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <div className="btn-group btn-group-sm" role="group" aria-label="Versão das regras">
            <span className="input-group-text border-0 bg-transparent small">Regras:</span>
            {(['v1', 'v2', 'v3'] as const).map((v) => (
              <button key={v} type="button" aria-pressed={v === 'v3'}
                      className={`btn ${v === 'v3' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setParams((prev) => {
                        // v1/v2 têm outros filtros e ordenações: recomeça limpo, mantendo o snapshot
                        const next = new URLSearchParams();
                        if (prev.get('snapshot')) next.set('snapshot', prev.get('snapshot') as string);
                        if (v !== 'v3') next.set('versao', v);  // v3 = por omissão (URL limpo)
                        return next;
                      })}>{v}</button>
            ))}
          </div>
          <select className="form-select form-select-sm" value={snapshot} aria-label="Snapshot"
                  onChange={(e) => update({ snapshot: e.target.value === meta?.snapshots[0]?.snapshot_date ? undefined : e.target.value })}>
            {meta?.snapshots.map((s) => (
              <option key={s.snapshot_date} value={s.snapshot_date}>
                {fmtDate(s.snapshot_date)} ({fmtInt(s.skus)} SKUs)
              </option>
            ))}
          </select>
          <a className={`btn btn-sm btn-outline-secondary ${!snapshot ? 'disabled' : ''}`}
             href={catalogApi.exportUrl(query)}
             title="Lista D1/D2 com os filtros activos (CSV com ; e UTF-8)">
            ⬇ CSV (lista D1/D2)
          </a>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* KPIs: D2 / D1 */}
      <div className="row g-2 mb-2">
        {(['D2', 'D1'] as CodV3[]).map((c) => (
          <div className="col-6 col-lg-3" key={c}>
            <div className={`card h-100 ${codFilter.includes(c) ? 'border-primary border-2' : ''}`}
                 role="button" onClick={() => toggle('cod', codFilter, c)}>
              <div className="card-body py-2">
                <div className="text-muted small">{COD_LABEL[c]}</div>
                <div className="fs-3 fw-semibold">{fmtInt(data?.counts_cod?.[c])}</div>
              </div>
            </div>
          </div>
        ))}
        <div className="col-6 col-lg-3">
          <div className={`card h-100 ${restock ? 'border-primary border-2' : ''}`} role="button"
               onClick={() => update({ restock: restock ? undefined : '1' })}>
            <div className="card-body py-2">
              <div className="text-muted small">Precisam de restock (D2 sem stock no T000)</div>
              <div className="fs-3 fw-semibold">{fmtInt(data?.counts_restock)}</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className={`card h-100 ${tProtegido ? 'border-primary border-2' : ''}`} role="button"
               onClick={() => update({ t_protegido: tProtegido ? undefined : '1' })}>
            <div className="card-body py-2">
              <div className="text-muted small">T000 abaixo de {fmtInt(minDays)} dias</div>
              <div className="fs-3 fw-semibold">{fmtInt(data?.counts_t_protegido)}</div>
            </div>
          </div>
        </div>
      </div>
      {/* KPIs: motivo */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {MOTIVO_ORDER.map((m) => (
          <button key={m} type="button"
                  className={`btn btn-sm ${motivoFilter.includes(m) ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => toggle('motivo', motivoFilter, m)}>
            {motivos[m] ?? m} <strong>{fmtInt(data?.counts_motivo?.[m])}</strong>
          </button>
        ))}
        <button type="button" className="btn btn-sm btn-link" onClick={() => setShowRules((v) => !v)}>
          {showRules ? 'Esconder regras em vigor' : 'Regras em vigor'}
        </button>
      </div>

      {showRules && (
        <div className="card mb-3"><div className="card-body">
          <h6 className="card-title">Regras em vigor (v3)</h6>
          <RulesPanelV3 p={meta?.parametros ?? {}} />
        </div></div>
      )}

      {/* Filtros */}
      <div className="card mb-3"><div className="card-body py-2">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-3">
            <label className="form-label small mb-1" htmlFor="catalog-search">Pesquisa (SKU ou nome)</label>
            <input id="catalog-search" className="form-control form-control-sm" value={searchInput}
                   onChange={(e) => setSearchInput(e.target.value)} placeholder="P040414, iPhone…" />
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small mb-1">Código</label>
            <select className="form-select form-select-sm" value={codFilter.length === 1 ? codFilter[0] : ''}
                    aria-label="Código"
                    onChange={(e) => update({ cod: e.target.value || undefined })}>
              <option value="">Todos</option>
              <option value="D2">D2 · Omnicanal</option>
              <option value="D1">D1 · Online</option>
            </select>
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small mb-1">Categoria 1</label>
            <select className="form-select form-select-sm" value={cat1}
                    onChange={(e) => update({ cat1: e.target.value || undefined })}>
              <option value="">Todas</option>
              {(meta?.cat1 ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small mb-1">Estado</label>
            <select className="form-select form-select-sm" value={estado}
                    onChange={(e) => update({ estado: e.target.value || undefined })}>
              <option value="">Todos</option>
              {(meta?.estados ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-12 col-md-3 d-flex gap-2 align-items-center">
            <span className="text-muted small">
              {loading ? 'A carregar…' : `${fmtInt(total)} artigos`}
              
            </span>
            {hasFilters && <button className="btn btn-sm btn-link p-0" onClick={resetFilters}>Limpar filtros</button>}
          </div>
        </div>
      </div></div>

      {/* Tabela */}
      <div className="table-responsive" style={{ opacity: loading ? 0.6 : 1 }}>
        <table className="table table-sm table-hover align-middle small">
          <thead className="table-light">
            <tr>
              <Th col="sku">SKU</Th>
              <Th col="nome">Nome</Th>
              <th>Categoria</th>
              <th>Estado</th>
              <th title="cod_distribuição proposto">Sugestão</th>
              <th>Motivo</th>
              <th>Ressalva</th>
              <Th col="lojas_procura" className="text-end" title="Lojas com procura (levantamentos + balcão) nos últimos 84 dias">Lojas c/ procura</Th>
              <Th col="procura" className="text-end" title="Encomendas de loja (levantamento + balcão), 84 dias">Enc. loja 84d</Th>
              <Th col="lojas" title="Lojas com stock hoje (verde); vermelho = sem stock mas com procura">Stock lojas</Th>
              <Th col="stock_t" className="text-end">Stock T000</Th>
              <th title={`Stock do T000 inferior a ${minDays} dias de vendas com envio`}>T000 &lt; {fmtInt(minDays)}d</th>
              <th>Restock</th>
              <Th col="vel" className="text-end" title="Vendas por dia (loja + envio), 28 dias">Vendas/dia 28d</Th>
            </tr>
          </thead>
          <tbody>
            {data?.results.map((r) => (
              <tr key={r.sku} role="button" onClick={() => update({ sku: r.sku }, { keepPage: true })}>
                <td><code>{r.sku}</code></td>
                <td style={{ maxWidth: 320 }}>{r.nome ?? '-'}</td>
                <td>{r.cat1}<div className="text-muted">{r.cat2}</div></td>
                <td>{r.estado || '-'}</td>
                <td><CodBadge cod={r.cod_distribuicao_v3} /></td>
                <td>{r.motivo_v3 ? motivos[r.motivo_v3] ?? r.motivo_v3 : '-'}</td>
                <td className="text-muted" style={{ maxWidth: 280 }}>{r.ressalva_v3 ?? ''}</td>
                <td className="text-end" title={(r.lojas_procura_v3 ?? []).join(', ')}>{fmtInt(r.n_lojas_procura_v3)}</td>
                <td className="text-end">{fmtInt(r.procura_loja_v3)}</td>
                <td><StoreDots row={r} /> <span className="text-muted">{r.lojas_com_stock}/6</span></td>
                <td className="text-end">{fmtInt(r.stock_T)}</td>
                <td>{r.t_protegido_v3 ? <Badge color="danger">sim</Badge> : <span className="text-muted">não</span>}</td>
                <td>{r.precisa_restock_v3 ? <Badge color="warning text-dark">sim</Badge> : <span className="text-muted">não</span>}</td>
                <td className="text-end">{fmtRate(r.vel_total_28d)}</td>
              </tr>
            ))}
            {data && data.results.length === 0 && (
              <tr><td colSpan={14} className="text-center text-muted py-4">
                Sem artigos para os filtros escolhidos (snapshots anteriores a 23/10/2026 não têm a v3).
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <span className="text-muted small">Página {page + 1} de {fmtInt(pages)}</span>
        <div className="btn-group btn-group-sm">
          <button className="btn btn-outline-secondary" disabled={page === 0 || loading}
                  onClick={() => setPage(0)}>«</button>
          <button className="btn btn-outline-secondary" disabled={page === 0 || loading}
                  onClick={() => setPage((p) => p - 1)}>‹ Anterior</button>
          <button className="btn btn-outline-secondary" disabled={page + 1 >= pages || loading}
                  onClick={() => setPage((p) => p + 1)}>Seguinte ›</button>
        </div>
      </div>

      {selectedSku && (
        <DetailPanel sku={selectedSku} snapshot={snapshot || undefined} onClose={() => update({ sku: undefined }, { keepPage: true })} />
      )}
    </div>
  );
};

export default ReplenishmentCatalogV3Page;
